import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { getToken, getRole } from "../utils/authStorage";

/**
 * Role-based default path when user is authenticated but not allowed on this route.
 */
function getDefaultPathForRole(role) {
  const r = (role || "").trim().toUpperCase();
  if (r === "ADMIN" || r === "ADMINISTRATOR") return "/dashboard";
  if (r === "LEGAL") return "/LegalDashboard";
  if (["QUALITY ASSURANCE", "EDITOR", "TRANSCRIPTIONIST"].includes(r)) return "/qa-dashboard";
  return "/login";
}

/**
 * Protects routes so that only authenticated users can access them.
 * If allowedRoles is provided, only users with one of those roles can access; others are redirected.
 * **tokenOnly** — require a JWT/session token only (legal-portal dashboard). Role is enforced by APIs; avoids
 * client redirect loops when login payloads omit or shape roles differently than getRole() expects.
 * Auth is stored in sessionStorage (per-tab).
 */
export default function ProtectedRoute({ children, allowedRoles, tokenOnly }) {
  const location = useLocation();
  const token = getToken();

  if (!token) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (tokenOnly) {
    return children;
  }

  const role = getRole();

  if (allowedRoles && Array.isArray(allowedRoles) && allowedRoles.length > 0) {
    const normalizedRole = (role || "").trim().toUpperCase();
    const allowed = allowedRoles.some((r) => (r || "").trim().toUpperCase() === normalizedRole);
    if (!allowed) {
      const redirectTo = getDefaultPathForRole(role);
      return <Navigate to={redirectTo} replace />;
    }
  }

  return children;
}
